import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ProjectService } from './project.service';
import { ProjectLossData } from '../models/project-loss-data';
import { ConnectivityEvent } from '../models/connectivity-event';

interface OfflineInterval {
  start: number; // unix ms of the offline edge
  end: number;   // unix ms of the online/caught-up edge, or +Infinity if still offline
}

/**
 * Derives the device's offline visual state, time-dimensioned to the scrubbed tick.
 *
 * Connectivity edge events (Connectivity_Offline/_Online/_CaughtUp) arrive in the loss data
 * (ProjectLossData.CE) with wall-clock timestamps. We reduce them to offline intervals and, on
 * each tick change, test whether the scrubbed tick's row timestamp falls inside an interval —
 * driving the red sidebar, the chart hatch overlays, and (on live transitions) toasts, all from
 * one source so they stay in sync as the slider scrubs.
 */
@Injectable({ providedIn: 'root' })
export class OfflineStateService {
  private intervals: OfflineInterval[] = [];
  private rowTimes: number[] = []; // LRC[i].UT, the per-tick wall-clock timeline
  private currentTick = 0;

  private readonly isOfflineAtTickSource = new BehaviorSubject<boolean>(false);
  /** True when the device was offline at the currently-scrubbed tick. */
  readonly isOfflineAtTick$ = this.isOfflineAtTickSource.asObservable();

  private readonly transitionSource = new Subject<'offline' | 'online'>();
  /** Fires on a real (live) offline<->online transition in the data — for toasts. */
  readonly transition$ = this.transitionSource.asObservable();

  private liveOffline = false; // is the most-recent interval still open (device offline now)
  private initialized = false; // suppress a toast on the very first data load

  constructor(private projectService: ProjectService) {
    this.projectService.lossData.subscribe((data) => this.onLossData(data));
    this.projectService.tickPosition.subscribe((tick) => this.onTick(tick));
  }

  private onLossData(data: ProjectLossData): void {
    this.rowTimes = (data?.LRC ?? []).map((r) => r.UT);
    const events = ((data?.CE ?? []).slice()).sort((a, b) => a.UT - b.UT);
    this.intervals = this.buildIntervals(events);

    // Live transition detection for toasts: the most recent interval being open means the
    // device is currently offline. Fire only on a change, and never on the first load.
    const nowOffline = this.intervals.length > 0 && !isFinite(this.intervals[this.intervals.length - 1].end);
    if (this.initialized && nowOffline !== this.liveOffline) {
      this.transitionSource.next(nowOffline ? 'offline' : 'online');
    }
    this.liveOffline = nowOffline;
    this.initialized = true;

    this.recompute();
  }

  private buildIntervals(events: ConnectivityEvent[]): OfflineInterval[] {
    const intervals: OfflineInterval[] = [];
    let openStart: number | null = null;
    for (const e of events) {
      if (e.D === 'Connectivity_Offline') {
        if (openStart === null) {
          openStart = e.UT;
        }
      } else {
        // Connectivity_Online / Connectivity_CaughtUp close an open offline interval.
        if (openStart !== null) {
          intervals.push({ start: openStart, end: e.UT });
          openStart = null;
        }
      }
    }
    if (openStart !== null) {
      intervals.push({ start: openStart, end: Number.POSITIVE_INFINITY });
    }
    return intervals;
  }

  private onTick(tick: number): void {
    this.currentTick = tick;
    this.recompute();
  }

  private recompute(): void {
    let offline = false;
    if (this.rowTimes.length > 0) {
      const idx = Math.min(Math.max(this.currentTick, 0), this.rowTimes.length - 1);
      const tickUT = this.rowTimes[idx];
      offline = tickUT != null && this.intervals.some((iv) => tickUT >= iv.start && tickUT < iv.end);
    }
    if (offline !== this.isOfflineAtTickSource.value) {
      this.isOfflineAtTickSource.next(offline);
    }
  }
}
